/**
 * ProductPricingPreview — Pricing preview section for ProductDetailModal.
 *
 * Displays a table of netto/VAT/brutto prices per billing interval variant,
 * with collapsible breakdown trees.
 *
 * @see AC-13, AC-15, AC-16, AC-17 in docs/specifications/2026-03-22-billing-admin-pricing-preview.md
 */
import React, { useState } from "react";
import type { AxiosError } from "axios";
import { usePricingPreviewQuery } from "../../../../hooks/useAdminProductCatalog";
import { formatCentsAsCurrency } from "./formatCalculatorConfig";
import type {
  ComponentBreakdown,
  PricingPreviewVariant,
} from "../../../../types/adminBilling";

// =============================================================================
// Constants
// =============================================================================

const BILLING_INTERVAL_LABELS: Record<string, string> = {
  monthly: "Miesięczny",
  quarterly: "Kwartalny",
  yearly: "Roczny",
};

// =============================================================================
// Breakdown tree component (AC-16)
// =============================================================================

interface BreakdownNodeProps {
  node: ComponentBreakdown;
  depth?: number;
}

const BreakdownNode: React.FC<BreakdownNodeProps> = ({ node, depth = 0 }) => {
  return (
    <>
      <div
        className="flex items-center justify-between text-xs text-neutral-700"
        style={{ paddingLeft: `${depth * 16}px` }}
      >
        <span>
          {depth > 0 && (
            <span className="text-neutral-300 mr-1">&mdash;</span>
          )}
          {node.name}
        </span>
        <span className="font-mono text-neutral-600">
          {formatCentsAsCurrency(node.contributionCents)}
        </span>
      </div>
      {node.children.map((child) => (
        <BreakdownNode
          key={child.componentId}
          node={child}
          depth={depth + 1}
        />
      ))}
    </>
  );
};

// =============================================================================
// Variant row with collapsible breakdown
// =============================================================================

interface VariantBreakdownProps {
  variant: PricingPreviewVariant;
}

const VariantBreakdown: React.FC<VariantBreakdownProps> = ({ variant }) => {
  const [showBreakdown, setShowBreakdown] = useState(false);

  return (
    <>
      <tr className="border-t border-neutral-100">
        <td className="px-4 py-2 text-sm text-neutral-800">
          {BILLING_INTERVAL_LABELS[variant.billingInterval] ??
            variant.billingInterval}
        </td>
        <td className="px-4 py-2 text-sm text-neutral-800 text-right font-mono">
          {formatCentsAsCurrency(variant.subtotalNetCents)}
        </td>
        <td className="px-4 py-2 text-sm text-neutral-500 text-right font-mono">
          {formatCentsAsCurrency(variant.vatCents)}
        </td>
        <td className="px-4 py-2 text-sm text-neutral-900 text-right font-mono font-medium">
          {formatCentsAsCurrency(variant.totalGrossCents)}
        </td>
      </tr>
      {/* AC-16: Collapsible breakdown */}
      <tr>
        <td colSpan={4} className="px-4 pb-1">
          <button
            type="button"
            aria-expanded={showBreakdown}
            onClick={() => setShowBreakdown(!showBreakdown)}
            className="text-xs text-neutral-500 hover:text-neutral-700 cursor-pointer"
          >
            {showBreakdown ? "Ukryj szczegóły" : "Pokaż szczegóły"}
          </button>
          {showBreakdown && variant.breakdown && (
            <div className="mt-1 mb-2 p-2 bg-neutral-50 rounded border border-neutral-200 space-y-0.5">
              <BreakdownNode node={variant.breakdown} />
            </div>
          )}
        </td>
      </tr>
    </>
  );
};

// =============================================================================
// Main component
// =============================================================================

export interface ProductPricingPreviewProps {
  productId: string;
  brandId: string;
  productType: string;
  pricingComponentId?: string | null;
}

/**
 * Pricing preview section for ProductDetailModal.
 *
 * Handles edge cases:
 * - PACKAGE products: shows info message (AC-17)
 * - No pricingComponentId: shows info message (AC-17)
 * - API error: shows error message (AC-17)
 */
export const ProductPricingPreview: React.FC<ProductPricingPreviewProps> = ({
  productId,
  brandId,
  productType,
  pricingComponentId,
}) => {
  const isPackage = productType === "PACKAGE";
  const hasPricingComponent = !!pricingComponentId;
  const shouldFetch = !isPackage && hasPricingComponent;

  const {
    data,
    isLoading,
    isError,
    error,
  } = usePricingPreviewQuery(productId, brandId, {
    enabled: shouldFetch,
  });

  // Render content below the common header
  const renderContent = () => {
    // AC-17: PACKAGE products
    if (isPackage) {
      return (
        <p className="text-sm text-neutral-500 italic">
          Preview cenowy dostępny tylko dla produktów LEAF
        </p>
      );
    }

    // AC-17: No pricing component
    if (!hasPricingComponent) {
      return (
        <p className="text-sm text-neutral-500 italic">
          Brak przypisanego komponentu cenowego
        </p>
      );
    }

    // Loading state
    if (isLoading) {
      return (
        <div className="flex items-center gap-2 text-sm text-neutral-500" role="status" aria-label="Ładowanie cennika">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
          <span>Ładowanie preview cenowego...</span>
        </div>
      );
    }

    // AC-17: API error
    if (isError) {
      const errorMessage = (
        (error as AxiosError<{ message?: string }>)?.response?.data?.message ??
        (error instanceof Error ? error.message : "Błąd ładowania preview cenowego")
      ).slice(0, 200);
      return <p className="text-sm text-red-600">{errorMessage}</p>;
    }

    const variants = data?.variants ?? [];

    if (variants.length === 0) {
      return (
        <p className="text-sm text-neutral-500 italic">
          Brak wariantów cenowych
        </p>
      );
    }

    return (
      <>
        <div className="bg-neutral-50 border border-neutral-200 rounded-md overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-neutral-100">
                <th className="px-4 py-2 text-left text-xs font-medium text-neutral-500 uppercase">
                  Interwał
                </th>
                <th className="px-4 py-2 text-right text-xs font-medium text-neutral-500 uppercase">
                  Netto
                </th>
                <th className="px-4 py-2 text-right text-xs font-medium text-neutral-500 uppercase">
                  VAT
                </th>
                <th className="px-4 py-2 text-right text-xs font-medium text-neutral-500 uppercase">
                  Brutto
                </th>
              </tr>
            </thead>
            <tbody>
              {variants.map((variant) => (
                <VariantBreakdown
                  key={variant.billingInterval}
                  variant={variant}
                />
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-neutral-400 mt-2 italic">
          (i) Ceny orientacyjne. Rabaty indywidualne dla tenantów nie są uwzględnione.
        </p>
      </>
    );
  };

  return (
    <div>
      <h4 className="text-sm font-semibold text-neutral-900 mb-2">Cennik</h4>
      {renderContent()}
    </div>
  );
};
